import React, { useEffect, useRef, useState } from "react";
import { NewTaskRow, TaskListHead, TaskRow } from "./TaskListTable";
import { SectionTime } from "./TaskBoard";

const COLLAPSED_KEY = "df.tasks.collapsedSections";

function readCollapsed() {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSED_KEY)) || {};
  } catch {
    return {};
  }
}

// List view of My Tasks grouped into the user's sections. Each section can
// be collapsed and has its own inline "add task" row.
export default function SectionedTaskList({
  groups,
  loading,
  selectedId,
  onOpen,
  onToggle,
  canAddTo,
  onCreateIn,
  draftRequest,
}) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [draft, setDraft] = useState(null); // { sectionId, name }
  const submitting = useRef(false);

  // The page header's "+ Add task" asks for a draft in a given section.
  useEffect(() => {
    if (!draftRequest) return;
    setCollapsed((prev) => ({ ...prev, [draftRequest.sectionId]: false }));
    setDraft({ sectionId: draftRequest.sectionId, name: "" });
  }, [draftRequest]);

  const toggleSection = (id) => {
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next));
      } catch {
        // storage unavailable — collapse state lasts for this visit only
      }
      return next;
    });
  };

  const submitDraft = async () => {
    const name = draft?.name.trim();
    if (!name) {
      setDraft(null);
      return;
    }
    // Clear the row before awaiting so a second Enter while the request is
    // in flight can't resubmit the same name and create a duplicate.
    if (submitting.current) return;
    submitting.current = true;
    const { sectionId } = draft;
    setDraft({ sectionId, name: "" });
    const group = groups.find((g) => g.section.id === sectionId);
    const ok = await onCreateIn(group.section, name);
    submitting.current = false;
    if (!ok) setDraft({ sectionId, name });
  };

  if (loading) return <p className="tk-muted tk-list-msg">Loading tasks…</p>;

  return (
    <div className="tk-sections" role="table" aria-label="Tasks">
      <div className="tk-sections-head">
        <TaskListHead />
      </div>
      {groups.map(({ section, tasks }) => {
        const isCollapsed = Boolean(collapsed[section.id]);
        const addable = canAddTo(section);
        return (
          <div
            key={section.id}
            role="rowgroup"
            className="tk-list tk-section-card"
          >
            <div className="tk-section-row" role="row">
              <button
                type="button"
                className="tk-section-toggle"
                onClick={() => toggleSection(section.id)}
                aria-expanded={!isCollapsed}
              >
                <span
                  className={`tk-caret ${isCollapsed ? "" : "open"}`}
                  aria-hidden="true"
                >
                  ▸
                </span>
                <span className="tk-section-name">{section.name}</span>
                <span className="tk-count">{tasks.length}</span>
                <SectionTime tasks={tasks} />
              </button>
              {addable && (
                <button
                  type="button"
                  className="tk-icon-btn tk-icon-btn-sm"
                  onClick={() => {
                    if (isCollapsed) toggleSection(section.id);
                    setDraft({ sectionId: section.id, name: "" });
                  }}
                  aria-label={`Add task to ${section.name}`}
                  title={`Add task to ${section.name}`}
                >
                  +
                </button>
              )}
            </div>
            {!isCollapsed && (
              <>
                {draft?.sectionId === section.id && (
                  <NewTaskRow
                    value={draft.name}
                    onChange={(name) =>
                      setDraft({ sectionId: section.id, name })
                    }
                    onSubmit={submitDraft}
                    onCancel={() => setDraft(null)}
                  />
                )}
                {tasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    selected={task.id === selectedId}
                    onOpen={onOpen}
                    onToggle={onToggle}
                  />
                ))}
                {tasks.length === 0 && draft?.sectionId !== section.id && (
                  <div className="tk-section-empty">No tasks</div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
